/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
} from '@angular/core';
import { XrxControlBaseComponent } from './xrx-control-base.component';
import { SettingType } from '../../../common/enums';
import { OptionListOptions, Parameter } from '../../services/workflow.service';

@Component({
  selector: 'xrx-setting',
  templateUrl: './xrx-setting.component.html',
  standalone: false,
})
export class XrxSettingComponent implements OnInit {
  @Input() setting: Parameter;
  @Input() isMfd = false;
  @Input() isAutomated = false;
  @Input() isPrint = false;
  @Input() sort = false;
  @Output() change = new EventEmitter<unknown>();
  @Output() toggle = new EventEmitter<boolean>();
  @Output() change2 = new EventEmitter<unknown>();
  @Output() validation = new EventEmitter<unknown>();
  @Output() focus = new EventEmitter<unknown>();
  @Output() blur = new EventEmitter<unknown>();
  @Output() paramChange = new EventEmitter<Parameter>();
  @Output() selectedValueChange = new EventEmitter<Parameter>();
  @Output() settingChange = new EventEmitter<Parameter>();

  @ViewChild(XrxControlBaseComponent) control: XrxControlBaseComponent;

  public get SettingType(): typeof SettingType {
    return SettingType;
  }

  ngOnInit() {
    this.setSelectedValue();
  }

  setSelectedValue() {
    if (this.setting.value) {
      this.setting.selectedValue = this.setValue(
        this.setting.value,
        this.setting.options,
        this.setting.type,
      );
    }

    if (this.setting.value2) {
      this.setting.selectedValue2 = this.setValue(
        this.setting.value2,
        this.setting.options2,
        this.setting.type,
      );
    }
  }

  private setValue(
    defaultValue: string,
    options: any[],
    type: SettingType,
  ): any {
    if (!options) {
      return null;
    }

    let valueToSet = null;
    if (type === SettingType.SelectWithGroup) {
      for (let i = 0; i < options.length; i++) {
        valueToSet = options[i].options.find(
          (option: any) => option.value === defaultValue,
        );
        if (valueToSet) {
          break;
        }
      }
    } else if (
      (type === SettingType.MultiSelect ||
        type === SettingType.MultiTranslation) &&
      defaultValue.includes('[')
    ) {
      valueToSet = JSON.parse(defaultValue);
      valueToSet.forEach((v: any) => {
        const opt = options.find((option) => option.value === v);
        if (opt) {
          opt.checked = true;
        }
      });
    } else if (type == SettingType.NumberCount) {
      valueToSet = { value: defaultValue };
    } else {
      valueToSet = options.find((option) => option.value === defaultValue);
    }

    return valueToSet;
  }

  relayChange($event: any) {
    this.setting.selectedValue = $event;
    this.setting.value = $event.value;
    this.change.emit($event);
  }

  relayToggle($event: Event | boolean) {
    this.setting.value = String($event);
    this.toggle.emit(Boolean($event));
  }

  relayQuantityChange($event: any) {
    (this.setting.selectedValue as OptionListOptions).value = $event.value;
    this.setting.value = $event.value;
    this.change.emit($event);
  }

  relayMultiSelectChange($event: any) {
    if (!this.setting.selectedValue) {
      this.setting.selectedValue = { key: '', value: '' };
    }

    if ($event.checked) {
      this.setting.selectedValue = $event.value;
    } else {
      this.setting.selectedValue = (
        this.setting.selectedValue as unknown as OptionListOptions[]
      ).filter((obj: any) => obj !== $event.value)[0];
    }

    this.setting.value = JSON.stringify(this.setting.selectedValue);
    this.change.emit($event);
  }

  relayChange2($event: any) {
    this.setting.selectedValue2 = $event;
    this.setting.value2 = $event.value;
    this.change2.emit($event);
  }

  relayFocus($event: Event) {
    this.focus.emit($event);
  }

  relayBlur($event: Event) {
    this.blur.emit($event);
  }

  validate<T>(e: T) {
    this.validation.emit(e);
  }
}
